<i18n>
{
	"en": {
		"details": "View Orders",
		"openTripSearch": "Open Trip Search",
		"closeTripSearch": "Close Trip Search",
		"trips": "No trips | {n} Trip | {n} Trips",
		"loadOrder": "Loadorder",
		"edit": "Edit",
		"trip": "Trip {n}",
		"newTrip": "New Trip",
		"failedToRemoveOrderTrip": "Failed to remove order from trip",
		"failedToRemoveOrderTransporter": "Failed to remove order from transporter",
		"orderRemovedFromTrip": "Order removed from trip",
		"orderRemovedFromTransporter": "Order removed from transporter",
		"loadingSequenceChanged": "Loading sequence saved",
		"failedToChangeLoadingSequence": "Failed to save loading sequence",
		"removeOrder": "Remove, but keep stretch",
		"editOrder": "Edit Order",
		"moveOrder": "Move Order to Different Trip",
		"failedToAddOrderToTrip": "Failed to add order to trip {number}",
		"orderAddedToTrip": "Order added to trip {number}",
		"orderAlreadyAdded": "A selected order is already part of the selected trip.",
		"validationErrors": "Trip is currently invalid, please edit trip before adding orders.",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"removeOrderAndMerge": "Remove",
		"ediScheduleOrder": "Schedule order for EDI",
		"ediUnScheduleOrder": "Unschedule order for EDI",
		"addToTransporter": "External Transporter",
		"openMapTripView": "Open trip map view"
	},
	"no": {
		"details": "Se oppdrag",
		"openTripSearch": "Åpne tursøk",
		"closeTripSearch": "Lukk tursøk",
		"trips": "Ingen turer | {n} Tur | {n} Turer",
		"loadOrder": "Lasterekkefølge",
		"edit": "Rediger",
		"trip": "Tur {n}",
		"newTrip": "Ny tur",
		"failedToRemoveOrderTrip": "Kunne ikke fjerne ordren fra turen",
		"failedToRemoveOrderTransporter": "Kunne ikke fjerne oppdrag fra transportøren",
		"orderRemovedFromTrip": "Oppdrag fjernet fra turen",
		"orderRemovedFromTransporter": "Oppdrag fjernet fra transportøren",
		"loadingSequenceChanged": "Lasterekkefølge lagret",
		"failedToChangeLoadingSequence": "Kunne ikke fjerne lasterekkefølge",
		"removeOrder": "Fjern og behold strekning",
		"editOrder": "Rediger oppdrag",
		"moveOrder": "Flytt oppdrag til annen tur",
		"failedToAddOrderToTrip": "Kunne ikke legge til oppdrag på turen {number}",
		"orderAddedToTrip": "Oppdrag lagt til tur {number}",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen.",
		"validationErrors": "Turen er ugyldig. Rediger turen før du legger til oppdrag.",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"removeOrderAndMerge": "Fjern",
		"ediScheduleOrder": "Planlegg ordre for EDI",
		"ediUnScheduleOrder": "Fjern planlagt EDI sending",
		"addToTransporter": "Ekstern leverandør",
		"openMapTripView": "Åpne turkartvisning"
	}
}
</i18n>

<template>
	<div class="map-trip-search">
		<map-bottom-bar
			v-if="tripDetails && tripDetailsVisible"
			v-droppable:tripDetails="handleDetailsDrop"
		>
			<template #actions>
				<t-panel-header
					appearance="minimal"
					type="bp"
					icon="arrow-left"
					color="blue"
					:text="$t('trip', {n: tripLabel})"
					@click="unsetTrip"
				/>
			</template>
			<template #info>
				<map-trip-search-info :trip="tripDetails" />
			</template>
			<template #toggles>
				<t-button
					:value="$t('newTrip')"
					appearance="minimal"
					type="bp"
					iconBefore="plus"
					@click="openNewTripModal"
				/>
				<t-button
					v-if="editableTrip"
					:value="$t('loadOrder')"
					appearance="minimal"
					iconBefore="arrow-down"
					iconAfter="more"
					@click="toggleChangeLoadingSequence(true)"
				/>
				<t-button
					v-if="tripDetails && !tripDetailsVisible"
					:value="$t('details')"
					iconBefore="application"
					appearance="minimal"
					@click="openTrip()"
				/>
				<t-button
					:value="tripSearchText"
					iconBefore="search-text"
					appearance="minimal"
					@click="$emit('toggleFilters')"
				/>
				<t-button
					v-if="editableTrip"
					:value="$t('edit')"
					appearance="minimal"
					iconBefore="annotation"
					@click="onEditTrip(tripDetails.id)"
				/>
			</template>
			<template #form>
				<map-trip-search-selected-truck :trip="tripDetails" />
			</template>
			<template #list>
				<template v-if="tripDetails.orders">
					<t-order-card
						v-for="order in sortedOrders"
						:key="order.id"
						style="margin: 0px 3px"
						:selected="isOrderSelected(order)"
						:orderHeader="getOrderHeader(order)"
						:orderNumber="order.orderNumber"
						:orderIcon="getOrderIcon(order)"
						:orderIconColor="getStatusColor(order.status)"
						:order="order"
						@select="selectOrder(order)"
						@open="openOrder(order)"
					>
						<template v-slot:contextMenu="{ item }">
							<t-menu>
								<t-menu-group>
									<t-menu-item
										icon="edit"
										@click="openOrder(item)"
									>
										<strong>{{ $t('editOrder') }}</strong>
									</t-menu-item>
									<t-menu-item
										icon="inheritance"
										@click="onMoveOrder(item)"
									>
										{{ $t('moveOrder') }}
									</t-menu-item>
									<t-menu-item
										v-if="!hasScheduleDate(item)"
										icon="edi"
										@click="onScheduleOrder({ subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.key })"
									>
										{{ $t('ediScheduleOrder') }}
									</t-menu-item>
									<t-menu-item
										v-if="orderInstructionScheduled(item)"
										icon="edi"
										@click="onUnscheduleOrder({ subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.key })"
									>
										{{ $t('ediUnScheduleOrder') }}
									</t-menu-item>
									<t-menu-item
										icon="cross"
										@click="onRemoveOrder(item,true)"
									>
										{{ $t('removeOrderAndMerge') }}
									</t-menu-item>
									<t-menu-item
										icon="cross"
										@click="onRemoveOrder(item)"
									>
										{{ $t('removeOrder') }}
									</t-menu-item>
									<hr>
									<t-menu-item
										icon="package-label"
										@click="showSinglePackageLabel(item.orderId)"
									>
										{{ $t('packageLabel') }}
									</t-menu-item>
									<t-menu-item
										icon="waybill"
										@click="showSingleWaybill(item.orderId)"
									>
										{{ $t('waybill') }}
									</t-menu-item>
								</t-menu-group>
							</t-menu>
						</template>
					</t-order-card>
				</template>
			</template>
		</map-bottom-bar>

		<map-bottom-bar v-else style="position:relative">
			<template #actions>
				<template v-if="!tripDetails">
					<t-panel-header
						icon="trip"
						iconType="tim"
						:text="$tc('trips', trips.length, {n: trips.length})"
					/>
				</template>
				<template v-if="tripDetails">
					<t-panel-header
						icon="trip"
						iconType="tim"
						:text="$t('trip', {n: tripLabel})"
					/>
					<div class="trip-list-summary">
						<t-metric-details
							:value="tripDetails.volume"
							decimals="1"
							hide-pie-icon
							unit="m3"
							size="small"
						/>
						<t-metric-details
							:value="tripDetails.grossWeight"
							hide-pie-icon
							unit="kg"
							size="small"
						/>
						<t-metric-details
							:value="tripDetails.loadingMeters"
							decimals="1"
							hide-pie-icon
							unit="lm"
							size="small"
						/>
					</div>
				</template>
			</template>

			<template #toggles>
				<t-button
					:value="$t('newTrip')"
					appearance="minimal"
					type="bp"
					iconBefore="plus"
					@click="openNewTripModal"
				/>
				<t-button
					v-if="editableTrip"
					:value="$t('loadOrder')"
					appearance="minimal"
					iconBefore="arrow-down"
					iconAfter="more"
					@click="toggleChangeLoadingSequence(true)"
				/>
				<t-button
					v-if="tripDetails && !tripDetailsVisible"
					:value="$t('details')"
					iconBefore="application"
					appearance="minimal"
					@click="openTrip()"
				/>
				<t-button
					:value="tripSearchText"
					iconBefore="search-text"
					appearance="minimal"
					@click="$emit('toggleFilters')"
				/>
				<t-button
					v-if="editableTrip"
					:value="$t('edit')"
					appearance="minimal"
					iconBefore="annotation"
					@click="onEditTrip(tripDetails.id)"
				/>
			</template>
			<template #form>
				<div v-if="dragging" class="drag-to-external">
					<t-card
						v-droppable:addExternal="(payload) => handleDrop(null, payload)"
						background="white"
						elevation="1"
						style="width: 240px; height: 110px;"
						border="default"
						class="external-card"
					>
						<div class="drag-to-external-card">
							<div class="drag-to-external-icon">
								<t-timpex-icon icon="new-link" type="bp" size="xl"/>
							</div>
							<div class="drag-to-external-text">
								{{$t('addToTransporter')}}
							</div>
						</div>
					</t-card>
				</div>
				<map-trip-search-form v-else />
			</template>
			<template #list>
				<template v-for="trip in trips">
					<t-trip-card
						v-if="trip.type.internalCode !== 'external'"
						:key="trip.id"
						v-droppable:[trip.id]="(payload) => handleDrop(trip, payload)"
						style="margin: 0px 3px; z-index:4"
						:selected="isTripSelected(trip)"
						:vehicleIcon="getIcon(trip)"
						:vehicleIconColor="getStatusColor(trip.status)"
						:trip="trip"
						scrollIntoViewOnSelect
						@select="selectTrip"
						@deselect="deselectTrip"
						@open="openTrip"
					>
						<template v-slot:contextMenu="{ item }">
							<t-menu>
								<t-menu-group>
									<t-menu-item icon="edit" @click="onEditTrip(item.id)"><strong>{{$t('edit')}}</strong></t-menu-item>
									<t-menu-item icon="inheritance" @click="onOpenMapTripView(item.id)">{{$t('openMapTripView')}}</t-menu-item>
								</t-menu-group>
							</t-menu>
						</template>
					</t-trip-card>
					<t-transporter-order-card
						v-if="trip.type.internalCode === 'external'"
						:key="trip.id"
						v-droppable:[trip.id]="(payload) => handleDrop(trip, payload)"
						style="margin: 0px 3px; z-index:4"
						:selected="isTripSelected(trip)"
						:transporterOrder="trip"
						@select="selectTrip"
						@open="openTrip"
					/>
				</template>
				<div
					v-droppable:tripList="handleListDrop"
					class="drop-zone"
				/>
			</template>
		</map-bottom-bar>
		<ConfirmRemoveTripOrder
			:isShown="confirmRemoveTripOrderVisible"
			:trip="tripDetails"
			@close="toggleRemoveTripOrder(false)"
			@confirm="confirmRemoveOrdersFromTrip"
		/>
		<ConfirmRemoveTransporterOrder
			:isShown="confirmRemoveTransporterOrderVisible"
			@close="toggleRemoveTransporterOrder(false)"
			@confirm="confirmRemoveOrdersFromTransporter"
		/>
		<ChangeLoadingSequence
			:isShown="changeLoadingSequenceVisible"
			:trip="tripDetails"
			@changeLoadingSequence="changeLoadingSequence"
			@close="toggleChangeLoadingSequence(false)"
		/>
		<MoveOrderToDifferentTripSelect
			:isShown="moveLoadedOrderVisible"
			:currentTrip="tripDetails"
			:order="selectedOrder"
			:disableSubmit="moveLoadedOrderProcessing"
			@close="moveLoadedOrderVisible = false"
			@moveOrder="onMoveOrderToDifferentTrip"
		/>
	</div>
</template>

<script>
import MapTripSearchForm from './mapTripSearch/MapTripSearchForm.vue';
import MapTripSearchSelectedTruck from './mapTripSearch/MapTripSearchSelectedTruck.vue';
import MapTripSearchInfo from './mapTripSearch/MapTripSearchInfo.vue';
import MapBottomBar from './MapBottomBar.vue';
import ConfirmRemoveTripOrder from '@/modules/tripSearch/modals/ConfirmRemoveTripOrder';
import ConfirmRemoveTransporterOrder from '@/modules/tripSearch/modals/ConfirmRemoveTransporterOrder';
import ChangeLoadingSequence from '@/modules/tripSearch/modals/ChangeLoadingSequence';
import MoveOrderToDifferentTripSelect from '@/modules/loadPlanningList/modals/MoveOrderToDifferentTripSelect';
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, cargoCarrierIcon, statuses, goodsTypesIcons
} from '@/core';
import moment from 'moment';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';
import TCard from '@/components/evergreen/layers/TCard';
import TTripCard from '@/components/evergreen/timpex/layers/TTripCard';
import TTransporterOrderCard from '@/components/evergreen/timpex/layers/TTransporterOrderCard';

export default {
	name: 'MapTripSearch',
	inject: ['namespace'],
	components: {
		TTransporterOrderCard,
		TTripCard,
		TCard,
		TMetricDetails,
		MapBottomBar,
		MapTripSearchForm,
		MapTripSearchSelectedTruck,
		MapTripSearchInfo,
		ConfirmRemoveTripOrder,
		ConfirmRemoveTransporterOrder,
		ChangeLoadingSequence,
		MoveOrderToDifferentTripSelect
	},

	inheritAttrs: false,
	props: { filtersVisible: Boolean },
	data() {
		return {
			hover: false,
			selectedOrder: null,
			confirmRemoveTripOrderVisible: false,
			confirmRemoveTransporterOrderVisible: false,
			changeLoadingSequenceVisible: false,
			moveLoadedOrderVisible: false,
			moveLoadedOrderProcessing: false,
			mergeOrder: false
		};
	},
	computed: {
		...mapDynamicState('computedTripSearchNamespace', [ 'trips', 'tripDetails', 'tripDetailsVisible' ]),
		...mapDynamicState('computedStoreNamespace', [ 'tripDetailsVisible' ]),
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		computedTripSearchNamespace() {
			return `loadPlanning/${this.namespace.id}/tripSearch`;
		},
		computedOrderSearchNamespace() {
			return `loadPlanning/${this.namespace.id}/orderSearch`;
		},
		tripSearchText() {
			return this.filtersVisible ? this.$t('closeTripSearch') : this.$t('openTripSearch');
		},
		editableTrip() {
			return this.tripDetails && this.tripDetails.type.internalCode !== 'external';
		},
		tripVendor() {
			return this.tripDetails && this.tripDetails.vendor ? this.tripDetails.vendor.name : '';
		},
		tripLabel() {
			return this.editableTrip ? this.tripDetails.tripNumber : this.tripVendor;
		},
		dragging() {
			return this.$store.getters['draggable/isDragging'];
		},
		sortedOrders() {
			let result = [];
			if (this.tripDetails && this.tripDetails.orders) {
				result = [...this.tripDetails.orders];
				result.sort((a,b) => {
					let aNum = isNaN(a.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : a.loadingSeqNo;
					let bNum = isNaN(b.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : b.loadingSeqNo;
					return aNum - bNum;
				});
			}
			return result;
		}
	},
	watch: {
		tripDetails: {
			immediate: true,
			handler(newVal, oldVal) {
				if ( (!oldVal && !newVal) || !newVal ) {
					this.setMapTripDetailsVisible(false);
				}
			}
		}
	},
	beforeDestroy() {
		window.removeEventListener('keyup', this.onRemoveOrderKeyPress);
	},
	mounted() {
		window.addEventListener('keyup', this.onRemoveOrderKeyPress);
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', [
			'removeOrdersFromTrip',
			'removeOrdersFromTransporter',
			'updateLoadingSequence',
			'moveOrderToDifferentTrip',
			'unscheduleTransporterOrderEdi',
			'scheduleTransporterOrderEdi',
			'getWaybill',
			'getPackageLabel',
			'setMapTripDetailsVisible'
		]),
		...mapDynamicActions('computedOrderSearchNamespace', ['setSelectedOriginalOrderById']),
		...mapDynamicActions('computedTripSearchNamespace', ['setTripDetails']),
		handleListDrop(orders) {
			if (!Array.isArray(orders)) {
				orders = [orders];
			}
			this.$emit('showAddToTransporter', { orders: orders });
		},
		handleDrop(trip, orders) {
			if (!Array.isArray(orders)) {
				orders = [orders];
			}
			if (!trip || trip.type.internalCode === 'external') {
				this.$emit('showAddToTransporter', { trip: trip, orders: orders });
			} else {
				this.$emit('showAddToTrip', { trip: trip, orders: orders });
			}
		},
		isOrderSelected(order) {
			return this.selectedOrder && this.selectedOrder.id === order.id;
		},
		isTripSelected(trip) {
			return this.tripDetails && this.tripDetails.id === trip.id;
		},
		openTrip(trip) {
			if (!trip && !this.tripDetails) {
				return;
			}
			if (trip && this.tripDetails && this.tripDetails.id !== trip.id) {
				this.setTripDetails(trip.id);
			}
			this.setMapTripDetailsVisible(true);
		},
		openOrder(order) {
			eventBus.$emit('setEditOrder',order.orderId);
		},
		deselectTrip() {
			this.setMapTripDetailsVisible(false);
			this.selectedOrder = null;
			this.setTripDetails(undefined);
		},
		selectTrip(trip) {
			if (trip && this.tripDetails && this.tripDetails.id === trip.id) return;
			this.setMapTripDetailsVisible(false);
			this.selectedOrder = null;
			this.setTripDetails(trip.id);
		},
		selectOrder(order) {
			if (order && this.selectedOrder && this.selectedOrder.id === order.id) {
				this.selectedOrder = null;
				this.setSelectedOriginalOrderById(undefined);
				return;
			}
			this.selectedOrder = order;
			this.setSelectedOriginalOrderById(order.orderId);
		},
		toggleHover(e) {
			this.hover = e.type === 'mouseenter';
		},
		unsetTrip() {
			this.setMapTripDetailsVisible(false);
			this.selectedOrder = null;
		},
		getIcon(trip) {
			if (!trip.cargoCarrier) return cargoCarrierIcon.getIcon(undefined);
			return cargoCarrierIcon.getIcon(trip.cargoCarrier.iconId);
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		getOrderIcon(order) {
			return goodsTypesIcons(order.goodsType);
		},
		getFrom(trip) {
			return {
				name: trip.fromAddress.name,
				place: `${trip.fromAddress.postalCode} ${trip.fromAddress.postalPlace}`,
				date: this.getDate(trip.etd, trip.atd)
			};
		},
		getTo(trip) {
			return {
				name: trip.toAddress.name,
				place: `${trip.toAddress.postalCode} ${trip.toAddress.postalPlace}`,
				date: this.getDate(trip.eta, trip.ata)
			};
		},
		getDate(estimated, actual) {
			let date = 'N/A';
			if (actual) {
				date = moment(actual.epoch).format('DD.MM');
			} else if (estimated) {
				date = moment(estimated.epoch).format('DD.MM');
			}

			return date;
		},
		getOrderHeader(order) {
			return order.customer ? order.customer.name : '';
		},
		onEditTrip(id) {
			eventBus.$emit('setEditTrip',id ? id : this.tripDetails.id);
			eventBus.$emit('toggleNewTripModal',true);
		},
		openNewTripModal() {
			eventBus.$emit('toggleNewTripModal',true);
		},
		handleDetailsDrop(payload) {
			if (!this.tripDetails || !this.tripDetailsVisible) return;
			this.handleDrop(this.tripDetails, payload);
		},
		toggleRemoveTransporterOrder(value) {
			this.confirmRemoveTransporterOrderVisible = value;
		},
		toggleRemoveTripOrder(value) {
			this.confirmRemoveTripOrderVisible = value;
		},
		toggleChangeLoadingSequence(value) {
			this.changeLoadingSequenceVisible = value;
		},
		onRemoveOrderKeyPress(e) {
			if (e.key !== 'Delete' || !this.selectedOrder || !this.tripDetails) return;
			this.onRemoveOrder(this.selectedOrder);
		},
		onRemoveOrder(order, merge = false) {
			if (!order || !this.tripDetails) return;
			this.selectedOrder = order;
			this.mergeOrder = merge;
			if (this.tripDetails.type.internalCode === 'external') {
				let timeCheck = moment.utc().subtract(5,'minutes')
					.valueOf();
				let hasSentOrders = this.selectedOrder.createdAt < timeCheck || this.selectedOrder.sentStatus;
				if (hasSentOrders)
					this.toggleRemoveTransporterOrder(true);
				else
					this.confirmRemoveOrdersFromTransporter();
			} else {
				this.toggleRemoveTripOrder(true);
			}
		},
		confirmRemoveOrdersFromTrip() {
			try {
				this.removeOrdersFromTrip({ orders: [this.selectedOrder], merge: this.mergeOrder });
				this.$notify({
					type: 'success',
					title: this.$t('orderRemovedFromTrip')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToRemoveOrderTrip')
				});
			}
		},
		confirmRemoveOrdersFromTransporter() {
			try {
				this.removeOrdersFromTransporter({ orders: [this.selectedOrder], merge: this.mergeOrder });
				this.$notify({
					type: 'success',
					title: this.$t('orderRemovedFromTransporter')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToRemoveOrderTransporter')
				});
			}
		},
		changeLoadingSequence(newSequence) {
			try {
				this.updateLoadingSequence(newSequence);
				this.toggleChangeLoadingSequence(false);
				this.$notify({
					type: 'success',
					title: this.$t('loadingSequenceChanged')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToChangeLoadingSequence')
				});
			}
		},
		async onMoveOrderToDifferentTrip(data) {
			this.moveLoadedOrderProcessing = true;
			try {
				await this.moveOrderToDifferentTrip(data);
				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: data.newTrip.tripNumber })}`
				});
				this.moveLoadedOrderVisible = false;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip', { number: data.newTrip.tripNumber }),
					duration: -1
				});
			}
			this.moveLoadedOrderProcessing = false;
		},
		onMoveOrder(order) {
			this.selectedOrder = order;
			this.moveLoadedOrderVisible = true;
		},
		orderInstructionScheduled(item) {
			if (!item.edi)
				return false;
			return !this.hasInstruction(item) && this.hasScheduleDate(item);
		},
		hasScheduleDate(item) {
			if (!item.edi)
				return false;
			return item.edi.ediSendingDate;
		},
		hasInstruction(item) {
			return Boolean(item.edi.out_edipartId);
		},

		async onScheduleOrder(obj) {
			await this.scheduleTransporterOrderEdi(obj);
		},
		async onUnscheduleOrder(obj) {
			await this.unscheduleTransporterOrderEdi(obj);
		},
		showSingleWaybill(id) {
			this.getWaybill([id]);
		},
		showSinglePackageLabel(id) {
			this.getPackageLabel([id]);
		},
		onOpenMapTripView(id) {
			let routeData = this.$router.resolve({ name: 'savedLoadPlanningMapTrip', params: { tripId: id } });
			window.open(routeData.href, '_blank');
		}
	}
};
</script>

<style lang="scss" scoped>

.map-trip-search {
	display: flex;
	height: 100%;
	grid-row: 2;
	z-index: 4;
	box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.1);
	overflow: hidden;
}
.trip-list-summary {
	display:grid;
	padding: 15px;
	grid-template-columns: repeat(3, max-content);
	grid-column-gap: 20px;
	margin-left: auto;
}
.drop-zone {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
}
.drag-to-external {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	padding-left: 10px;
	padding-right: 10px;
	position: relative;
	width:204px;
	text-align: center;
	&::after {
		content: '';
		width: 1px;
		height: 110px;
		background: #e4e7eb;
		position: absolute;
		top: 0;
		right: 0;
	}
	.external-card:hover {
		box-shadow: 0 0 2px rgba(67, 90, 111, 0.14), 0 0 0 1px #579ad9, 0 0 0 3px rgba(16, 112, 202, 0.14);
	}
	.drag-to-external-card {
		align-self: center;
		justify-content: center;
		display: flex;
		flex-direction: column;
		align-items: center;
		height: 100%;
	}
}

</style>
